import React from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type AppDialogVariant =
    | 'success'
    | 'error'
    | 'warning'
    | 'info'
    | 'confirm';

type AppDialogMessageProps = {
    open: boolean;
    title?: string;
    message: React.ReactNode;
    variant?: AppDialogVariant;
    onClose: () => void;
    onConfirm?: () => void;
    confirmText?: string;
    cancelText?: string;
    hideCancel?: boolean;
    loading?: boolean;
    maxWidth?: 'xs' | 'sm' | 'md';
    disableBackdropClose?: boolean;
    children?: React.ReactNode;
};

const variantConfig: Record<
    AppDialogVariant,
    {
        icon: React.ReactNode;
        color: string;
        buttonColor: 'success' | 'error' | 'warning' | 'info' | 'primary';
        defaultTitle: string;
    }
> = {
    success: {
        icon: <CheckCircleOutlineIcon sx={{ fontSize: 34 }} />,
        color: '#2e7d32',
        buttonColor: 'success',
        defaultTitle: 'Success',
    },
    error: {
        icon: <ErrorOutlineIcon sx={{ fontSize: 34 }} />,
        color: '#d32f2f',
        buttonColor: 'error',
        defaultTitle: 'Error',
    },
    warning: {
        icon: <WarningAmberRoundedIcon sx={{ fontSize: 34 }} />,
        color: '#ed6c02',
        buttonColor: 'warning',
        defaultTitle: 'Warning',
    },
    info: {
        icon: <InfoOutlinedIcon sx={{ fontSize: 34 }} />,
        color: '#0288d1',
        buttonColor: 'info',
        defaultTitle: 'Information',
    },
    confirm: {
        icon: <HelpOutlineIcon sx={{ fontSize: 34 }} />,
        color: '#456491',
        buttonColor: 'primary',
        defaultTitle: 'Please Confirm',
    },
};

export default function AppDialogMessage({
    open,
    title,
    message,
    variant = 'info',
    onClose,
    onConfirm,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    hideCancel = false,
    loading = false,
    maxWidth = 'sm',
    disableBackdropClose = false,
    children,
}: AppDialogMessageProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const config = variantConfig[variant];

    const handleClose = (_?: unknown, reason?: string) => {
        if (disableBackdropClose && reason === 'backdropClick') return;
        if (loading) return;
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth={maxWidth}
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    overflow: 'hidden',
                },
            }}
        >
            <DialogTitle
                sx={{
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background:
                        'linear-gradient(180deg, rgba(69,100,145,0.08) 0%, rgba(69,100,145,0.02) 100%)',
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                        sx={{
                            width: 46,
                            height: 46,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            backgroundColor: `${config.color}14`,
                            color: config.color,
                            flexShrink: 0,
                        }}
                    >
                        {config.icon}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={700}>
                            {title || config.defaultTitle}
                        </Typography>
                    </Box>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 3 }}>
                <Stack spacing={2}>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ whiteSpace: 'pre-wrap' }}
                    >
                        {message}
                    </Typography>

                    {children}
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    flexDirection: isMobile ? 'column-reverse' : 'row',
                    gap: 1,
                }}
            >
                {!hideCancel && (
                    <Button
                        fullWidth={isMobile}
                        variant="outlined"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {cancelText}
                    </Button>
                )}

                <Button
                    fullWidth={isMobile}
                    variant="contained"
                    color={config.buttonColor}
                    onClick={onConfirm || onClose}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {loading ? 'Please wait...' : confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    );
}